/**
 * Rendez-vous — ma page de prise de rendez-vous (eventmatch.pro/<pseudo>),
 * mes disponibilités, Google Agenda, mes rendez-vous à venir ; puis ce qui
 * attend une réponse, les matchs, l'annuaire, et mes règles.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Check, Copy, ExternalLink, Plus, X } from 'lucide-react';
import { envoyer, appelerApi } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import { CarteEntrepreneur } from './CarteEntrepreneur.tsx';
import {
  JOURS_SEMAINE,
  dateLongue,
  heure,
  heureVersMinutes,
  minutesVersHeure,
  nomDe,
  type Carte,
  type Disponibilite,
  type Espace,
  type EvenementAVenir,
  type MonAgenda,
} from './types.ts';

type Onglet = string; // 'agenda' | 'demandes' | 'matchs' | 'annuaire' | <evenementId>

export function RendezVous({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const [evenements, setEvenements] = useState<EvenementAVenir[]>([]);
  const [onglet, setOnglet] = useState<Onglet>(espace.demandes_en_attente > 0 ? 'demandes' : 'agenda');
  const [profils, setProfils] = useState<Carte[] | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    void appelerApi<{ evenements: EvenementAVenir[] }>('/api/compte/rendez-vous').then((r) => {
      const liste = r.donnees?.evenements ?? [];
      setEvenements(liste);
      void liste;
    });
  }, [espace.demandes_en_attente]);

  const charger = useCallback(async () => {
    setProfils(null);
    if (onglet === 'agenda') {
      setProfils([]);
      return;
    }
    if (onglet === 'demandes') {
      const r = await appelerApi<{ demandes: Carte[] }>('/api/compte/demandes');
      setProfils(r.donnees?.demandes ?? []);
    } else if (onglet === 'matchs') {
      const r = await appelerApi<{ matchs: Carte[] }>('/api/compte/matchs');
      setProfils(r.donnees?.matchs ?? []);
    } else if (onglet === 'annuaire') {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
      const r = await appelerApi<{ profils: Carte[] }>(`/api/compte/annuaire${params}`);
      setProfils(r.donnees?.profils ?? []);
    } else {
      const r = await appelerApi<{ profils: Carte[] }>(`/api/compte/rendez-vous/${onglet}`);
      setProfils(r.donnees?.profils ?? []);
    }
  }, [onglet, q]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const evenementChoisi = evenements.find((e) => e.evenement_id === onglet);
  const rafraichir = async () => {
    await Promise.all([charger(), onChange()]);
  };

  return (
    <>
      <PageTitle
        title={
          <>
            Rendez-<em className="text-match-gradient font-serif italic font-medium">vous</em>
          </>
        }
        subtitle="Votre page de prise de rendez-vous, votre agenda, et ceux qui veulent vous voir. Ce qui compte n’est pas le score, c’est la raison de s’appeler."
      />

      <div className="mb-5 flex flex-wrap gap-2">
        <Pastille actif={onglet === 'agenda'} onClick={() => setOnglet('agenda')}>
          Mes rendez-vous
        </Pastille>
        <Pastille actif={onglet === 'demandes'} onClick={() => setOnglet('demandes')}>
          Demandes reçues{espace.demandes_en_attente > 0 ? ` (${espace.demandes_en_attente})` : ''}
        </Pastille>
        <Pastille actif={onglet === 'matchs'} onClick={() => setOnglet('matchs')}>
          Mes matchs
        </Pastille>
        {evenements
          .filter((e) => e.inscrits_avec_compte > 0)
          .map((e) => (
            <Pastille key={e.evenement_id} actif={onglet === e.evenement_id} onClick={() => setOnglet(e.evenement_id)}>
              Avant « {e.nom} » ({e.inscrits_avec_compte})
            </Pastille>
          ))}
        <Pastille actif={onglet === 'annuaire'} onClick={() => setOnglet('annuaire')}>
          Tous les entrepreneurs
        </Pastille>
      </div>

      {evenementChoisi && (
        <p className="mb-5 max-w-[640px] text-[14px] leading-relaxed text-fumee">
          {evenementChoisi.inscrits_avec_compte} personne(s) inscrite(s) comme vous à «{' '}
          {evenementChoisi.nom} ». Prenez rendez-vous maintenant : une conversation calée à l’avance
          vaut mieux qu’une carte de visite ramassée au vestiaire.
        </p>
      )}

      {onglet === 'annuaire' && (
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, entreprise, métier, ce qu’ils proposent…"
          className="field-input mb-5 !min-h-[40px] !py-2 !text-sm"
        />
      )}

      {onglet === 'agenda' && <MonAgendaPerso espace={espace} />}

      {onglet !== 'agenda' && profils === null && <p className="text-[14px] text-fumee">Chargement…</p>}
      {onglet !== 'agenda' && profils?.length === 0 && <p className="max-w-[560px] text-[14px] leading-relaxed text-fumee">{vide(onglet)}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        {profils?.map((p) => (
          <CarteEntrepreneur
            key={p.personne_id}
            carte={p}
            evenementId={evenementChoisi?.evenement_id ?? null}
            onChange={() => void rafraichir()}
          />
        ))}
      </div>

      <Reglages espace={espace} onChange={onChange} />
    </>
  );
}

/* ————————————————— Mon agenda : ma page, mes disponibilités, Google, mes rendez-vous ————————————————— */

function MonAgendaPerso({ espace }: { espace: Espace }) {
  const [agenda, setAgenda] = useState<MonAgenda | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    const r = await appelerApi<MonAgenda>('/api/agenda/moi');
    setAgenda(r.donnees);
  }, []);
  useEffect(() => {
    void charger();
    const etat = new URLSearchParams(location.search).get('google');
    if (etat === 'ok') setMessage('Google Agenda est connecté : vos rendez-vous y seront posés.');
    if (etat === 'refuse') setMessage('Connexion Google annulée.');
    if (etat === 'erreur') setMessage('La connexion Google a échoué — réessayez, ou vérifiez la configuration côté plateforme.');
  }, [charger]);

  if (!agenda) return <p className="text-[14px] text-fumee">Chargement…</p>;

  const url = espace.profil?.pseudo ? `${location.origin}/${espace.profil.pseudo}` : null;
  const aVenir = agenda.rendez_vous.filter((r) => r.statut === 'confirme' && new Date(r.debut_le) > new Date());
  const passes = agenda.rendez_vous.filter((r) => r.statut !== 'confirme' || new Date(r.debut_le) <= new Date());

  return (
    <div className="flex flex-col gap-4">
      {message && <p className="text-[13px] text-blanc">{message}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <MaPage url={url} quota={agenda.quota} />
        <GoogleAgenda etat={agenda.google} onChange={charger} />
      </div>

      <Disponibilites initiales={agenda.disponibilites} onChange={charger} />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SectionLabel>À venir</SectionLabel>
          <span className="text-[13px] text-fumee">
            {agenda.quota.a_venir}
            {agenda.quota.limite !== null ? ` / ${agenda.quota.limite}` : ''} rendez-vous pris sur votre page
          </span>
        </div>
        {aVenir.length === 0 ? (
          <p className="mt-3 max-w-[560px] text-[14px] leading-relaxed text-fumee">
            Aucun rendez-vous à venir. Partagez votre page : en signature d’e-mail, sur LinkedIn, sur votre stand.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-3">
            {aVenir.map((r) => (
              <LigneRdv key={r.id} r={r} onAnnuler={() => void envoyer(`/api/agenda/rendez-vous/${r.id}/annuler`, {}).then(charger)} />
            ))}
          </div>
        )}
        {passes.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-[13px] text-fumee">Passés et annulés ({passes.length})</summary>
            <div className="mt-3 flex flex-col gap-2 opacity-70">
              {passes.map((r) => (
                <LigneRdv key={r.id} r={r} />
              ))}
            </div>
          </details>
        )}
      </Card>
    </div>
  );
}

function LigneRdv({ r, onAnnuler }: { r: MonAgenda['rendez_vous'][number]; onAnnuler?: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-graphite pb-3 last:border-0 last:pb-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-[14px] text-blanc">
          <Calendar size={14} strokeWidth={1.5} />
          {dateLongue(r.debut_le)} · {heure(r.debut_le)} – {heure(r.fin_le)}
          {r.statut === 'annule' && <Badge>Annulé</Badge>}
          {r.dans_google && <Badge>Google Agenda</Badge>}
        </p>
        <p className="mt-1 text-[13px] text-fumee">
          {r.je_suis_hote ? 'Avec' : 'Chez'} <span className="text-blanc">{r.entreprise ?? nomDe(r)}</span>
          {r.entreprise ? ` · ${nomDe(r)}` : ''}
          {r.email ? ` · ${r.email}` : ''}
          {r.message ? ` — « ${r.message} »` : ''}
        </p>
      </div>
      {onAnnuler && r.statut === 'confirme' && <Btn onClick={onAnnuler}>Annuler</Btn>}
    </div>
  );
}

function MaPage({ url, quota }: { url: string | null; quota: MonAgenda['quota'] }) {
  const [copie, setCopie] = useState(false);
  return (
    <Card>
      <SectionLabel>Ma page de rendez-vous</SectionLabel>
      {url ? (
        <>
          <div className="mt-3 flex items-center gap-2 rounded-[8px] border border-graphite px-3 py-2">
            <span className="truncate text-[14px] text-blanc">{url.replace(/^https?:\/\//, '')}</span>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(url).catch(() => undefined);
                setCopie(true);
                setTimeout(() => setCopie(false), 2000);
              }}
              aria-label="Copier"
              className="ml-auto shrink-0 text-fumee hover:text-blanc"
            >
              {copie ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
            </button>
            <a href={url} target="_blank" rel="noreferrer" aria-label="Voir ma page" className="shrink-0 text-fumee hover:text-blanc">
              <ExternalLink size={14} strokeWidth={1.5} />
            </a>
          </div>
          <p className="mt-3 text-[13px] leading-relaxed text-fumee">
            Type Calendly, avec en plus votre influence, votre entreprise et vos services. Le même lien inscrit
            et parraine ceux qui n’ont pas encore de compte ; <span className="text-blanc">{url.replace(/^https?:\/\//, '')}/join</span>{' '}
            va droit à l’inscription.
          </p>
        </>
      ) : (
        <p className="mt-3 text-[14px] leading-relaxed text-fumee">
          Choisissez d’abord votre nom d’adresse dans{' '}
          <Link to="/compte/entreprise#adresse" className="text-blanc underline">Moi et mon entreprise</Link> — par exemple{' '}
          <span className="text-blanc">eventmatch.pro/tomwart</span>.
        </p>
      )}
      <p className="mt-3 text-[12px] leading-relaxed text-fumee">
        {quota.limite === null
          ? 'Rendez-vous illimités (formule Pro ou Premium).'
          : `En gratuit, ${quota.limite} rendez-vous à venir au maximum ; au-delà, votre page invite le visiteur à vous recommander Premium. Vous en avez ${quota.a_venir}.`}
      </p>
    </Card>
  );
}

function GoogleAgenda({ etat, onChange }: { etat: MonAgenda['google']; onChange: () => Promise<void> }) {
  const [enCours, setEnCours] = useState(false);
  const connecter = async () => {
    setEnCours(true);
    const r = await appelerApi<{ url: string }>('/api/agenda/google/url');
    if (r.donnees?.url) location.assign(r.donnees.url);
    else setEnCours(false);
  };
  return (
    <Card>
      <SectionLabel>Google Agenda</SectionLabel>
      {etat.connexion ? (
        <>
          <p className="mt-3 flex items-center gap-2 text-[14px] text-blanc">
            <Check size={14} strokeWidth={1.5} /> Connecté{etat.connexion.email ? ` — ${etat.connexion.email}` : ''}
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-fumee">
            Chaque rendez-vous pris sur votre page est posé dans votre agenda, avec l’invité en participant, et vos
            plages déjà occupées ne sont plus proposées.
          </p>
          {etat.connexion.derniere_erreur && (
            <p className="mt-2 text-[12px]" style={{ color: 'var(--em-erreur)' }}>
              Dernière erreur Google : {etat.connexion.derniere_erreur}. Reconnectez-vous si elle persiste.
            </p>
          )}
          <Btn className="mt-3" onClick={() => void envoyer('/api/agenda/google', {}, 'DELETE').then(onChange)}>
            Déconnecter
          </Btn>
        </>
      ) : (
        <>
          <p className="mt-3 text-[14px] leading-relaxed text-fumee">
            Connectez votre compte Google : vos rendez-vous s’inscrivent dans votre agenda, et vos créneaux déjà
            pris ne sont plus proposés.
          </p>
          {etat.configure ? (
            <Btn variant="primary" className="mt-3" onClick={() => void connecter()} disabled={enCours}>
              {enCours ? 'Redirection…' : 'Connecter Google Agenda'}
            </Btn>
          ) : (
            <p className="mt-3 text-[12px] text-fumee">
              La connexion Google n’est pas encore activée sur la plateforme — vos rendez-vous restent visibles
              ici et vous arrivent par e-mail.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

const HEURES = Array.from({ length: 29 }, (_, i) => 7 * 60 + i * 30).filter((m) => m <= 21 * 60); // 07:00 → 21:00

function Disponibilites({ initiales, onChange }: { initiales: Disponibilite[]; onChange: () => Promise<void> }) {
  const [liste, setListe] = useState<Disponibilite[]>(initiales);
  const [etat, setEtat] = useState<'repos' | 'envoi' | 'ok'>('repos');

  const enregistrer = async () => {
    setEtat('envoi');
    await envoyer('/api/agenda/disponibilites', { disponibilites: liste.map(({ id: _id, ...d }) => d) }, 'PUT');
    await onChange();
    setEtat('ok');
    setTimeout(() => setEtat('repos'), 2000);
  };
  const ordre = [1, 2, 3, 4, 5, 6, 0];

  return (
    <Card>
      <SectionLabel>Mes disponibilités</SectionLabel>
      <p className="mt-2 max-w-[640px] text-[13px] leading-relaxed text-fumee">
        Ce que votre page propose chaque semaine, heure de Bruxelles. Les rendez-vous déjà pris — ici ou dans
        Google Agenda — disparaissent des créneaux automatiquement.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        {ordre.map((jour) => {
          const plages = liste.filter((d) => d.jour_semaine === jour);
          return (
            <div key={jour} className="flex flex-wrap items-start gap-3 border-b border-graphite pb-3 last:border-0">
              <span className="w-24 shrink-0 pt-2 text-[14px] text-blanc">{JOURS_SEMAINE[jour]}</span>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {plages.length === 0 && <span className="pt-2 text-[13px] text-fumee">—</span>}
                {plages.map((p) => {
                  const i = liste.indexOf(p);
                  const maj = (patch: Partial<Disponibilite>) => setListe(liste.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <select value={minutesVersHeure(p.debut_min)} onChange={(e) => maj({ debut_min: heureVersMinutes(e.target.value) })} className="field-input !min-h-[36px] !w-auto !py-1 !text-sm">
                        {HEURES.map((m) => <option key={m} value={minutesVersHeure(m)} className="bg-charbon">{minutesVersHeure(m)}</option>)}
                      </select>
                      <span className="text-[13px] text-fumee">à</span>
                      <select value={minutesVersHeure(p.fin_min)} onChange={(e) => maj({ fin_min: heureVersMinutes(e.target.value) })} className="field-input !min-h-[36px] !w-auto !py-1 !text-sm">
                        {HEURES.filter((m) => m > p.debut_min).map((m) => <option key={m} value={minutesVersHeure(m)} className="bg-charbon">{minutesVersHeure(m)}</option>)}
                      </select>
                      <span className="text-[13px] text-fumee">par</span>
                      <select value={p.duree_min} onChange={(e) => maj({ duree_min: Number(e.target.value) })} className="field-input !min-h-[36px] !w-auto !py-1 !text-sm">
                        {[15, 20, 30, 45, 60].map((d) => <option key={d} value={d} className="bg-charbon">{d} min</option>)}
                      </select>
                      <button onClick={() => setListe(liste.filter((_, j) => j !== i))} aria-label="Retirer" className="text-fumee hover:text-blanc">
                        <X size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                  );
                })}
                <button
                  onClick={() => setListe([...liste, { jour_semaine: jour, debut_min: 9 * 60, fin_min: 12 * 60, duree_min: 30 }])}
                  className="inline-flex items-center gap-1 self-start text-[13px] text-fumee hover:text-blanc"
                >
                  <Plus size={13} strokeWidth={1.5} /> Ajouter une plage
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Btn variant="primary" onClick={() => void enregistrer()} disabled={etat === 'envoi'}>
          {etat === 'envoi' ? 'Enregistrement…' : 'Enregistrer mes disponibilités'}
        </Btn>
        {etat === 'ok' && <span className="flex items-center gap-1.5 text-[13px] text-blanc"><Check size={14} strokeWidth={1.5} /> Votre page est à jour</span>}
      </div>
    </Card>
  );
}

function vide(onglet: Onglet): string {
  if (onglet === 'demandes') return 'Aucune demande en attente. Quand quelqu’un veut vous rencontrer, elle apparaît ici.';
  if (onglet === 'matchs') return 'Aucun match pour l’instant. Un match se crée quand vous acceptez une demande — ou qu’on accepte la vôtre.';
  return 'Personne à afficher ici pour l’instant.';
}

function Pastille({ actif, onClick, children }: { actif: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border border-graphite px-4 py-2 text-[13px] transition-colors ${actif ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}
    >
      {children}
    </button>
  );
}

/* ————————————————— Jusqu'où j'accepte qu'on m'approche ————————————————— */

function Reglages({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const p = espace.profil;
  const [etat, setEtat] = useState({
    visibleAnnuaire: p?.visible_annuaire ?? true,
    contactAvantEvenement: p?.contact_avant_evenement ?? true,
    contactApresEvenement: p?.contact_apres_evenement ?? true,
    visibiliteProfil: p?.visibilite_profil ?? 'toujours',
    speedMeetings: p?.speed_meetings ?? true,
    navigationDiscrete: p?.navigation_discrete ?? false,
  });
  const [message, setMessage] = useState<string | null>(null);

  const poser = async (patch: Partial<typeof etat>) => {
    const suivant = { ...etat, ...patch };
    setEtat(suivant);
    const r = await envoyer('/api/compte/moi', patch, 'PUT');
    setMessage(r.erreur ?? 'Enregistré ✓');
    if (!r.erreur) await onChange();
  };

  return (
    <section className="mt-10">
      <h2 className="text-[18px] font-light text-blanc">Comment on peut vous approcher</h2>
      <p className="mb-4 mt-1 max-w-[640px] text-[13px] leading-relaxed text-fumee">
        Vos règles, appliquées partout : personne ne vous voit ni ne vous écrit en dehors de ce que
        vous autorisez ici.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Visibilité</SectionLabel>
          <Bascule
            coche={etat.visibleAnnuaire}
            onChange={(v) => void poser({ visibleAnnuaire: v })}
            titre="Apparaître dans l’annuaire"
            texte="Les autres comptes voient votre profil. Décochez pour disparaître complètement."
          />
          <div className="mt-4 border-t border-graphite pt-4">
            <p className="text-[14px] text-blanc">Qui voit vos coordonnées</p>
            <div className="mt-2 flex flex-col gap-2">
              <Radio
                nom="visibilite"
                coche={etat.visibiliteProfil === 'toujours'}
                onChange={() => void poser({ visibiliteProfil: 'toujours' })}
                titre="Tout de suite"
                texte="Votre profil et vos coordonnées sont visibles des autres comptes."
              />
              <Radio
                nom="visibilite"
                coche={etat.visibiliteProfil === 'apres_match'}
                onChange={() => void poser({ visibiliteProfil: 'apres_match' })}
                titre="Seulement après match"
                texte="On voit votre profil, mais vos coordonnées n’apparaissent qu’une fois la rencontre acceptée par vous."
              />
            </div>
          </div>
          <div className="mt-4 border-t border-graphite pt-4">
            <Bascule
              coche={etat.navigationDiscrete}
              onChange={(v) => void poser({ navigationDiscrete: v })}
              titre="Navigation discrète"
              texte="Vos visites de profils restent anonymes — en contrepartie, vous apparaissez comme « visiteur discret » chez les autres."
            />
          </div>
        </Card>

        <Card>
          <SectionLabel>Contact autour des événements</SectionLabel>
          <Bascule
            coche={etat.contactAvantEvenement}
            onChange={(v) => void poser({ contactAvantEvenement: v })}
            titre="Avant l’événement"
            texte="Les autres inscrits peuvent vous solliciter dès l’inscription, pour caler un rendez-vous sur place."
          />
          <div className="mt-4">
            <Bascule
              coche={etat.contactApresEvenement}
              onChange={(v) => void poser({ contactApresEvenement: v })}
              titre="Après l’événement"
              texte="On peut vous recontacter une fois l’événement passé, pour donner suite."
            />
          </div>
          <div className="mt-4 border-t border-graphite pt-4">
            <Bascule
              coche={etat.speedMeetings}
              onChange={(v) => void poser({ speedMeetings: v })}
              titre="Speed meetings de 5 minutes"
              texte="Vous acceptez les rendez-vous courts organisés sur place au Meet Corner."
            />
          </div>
        </Card>
      </div>
      {message && (
        <p className="mt-3 text-[13px] text-fumee" role="status">
          {message}
        </p>
      )}
    </section>
  );
}

function Bascule({ coche, onChange, titre, texte }: { coche: boolean; onChange: (v: boolean) => void; titre: string; texte: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input type="checkbox" checked={coche} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
      <span>
        <span className="block text-[14px] text-blanc">{titre}</span>
        <span className="mt-0.5 block text-[13px] leading-relaxed text-fumee">{texte}</span>
      </span>
    </label>
  );
}

function Radio({ nom, coche, onChange, titre, texte }: { nom: string; coche: boolean; onChange: () => void; titre: string; texte: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input type="radio" name={nom} checked={coche} onChange={onChange} className="mt-1" />
      <span>
        <span className="block text-[14px] text-blanc">{titre}</span>
        <span className="mt-0.5 block text-[13px] leading-relaxed text-fumee">{texte}</span>
      </span>
    </label>
  );
}
